import React from "react";
import Typography from "@mui/material/Typography";
import { StatCard } from "@outpave/ds";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatCashFull } from "utils/formatCash";
import { Columns } from "components/layout/reusableLayouts";
import type { QueueSummary } from "hooks/useDashboardQueues";

interface QueueCardsProps {
  cardRequests: QueueSummary;
  payments: QueueSummary;
  invoices: QueueSummary;
  reimbursements: QueueSummary;
  rewards: QueueSummary;
  /**
   * The company's currency, from the same source the insights that rank these
   * queues read. `formatCashFull` prints dollars when handed nothing.
   */
  currency: string;
  /** Payments, invoices and rewards are gated, so their tiles come and go. */
  showPayments?: boolean;
  showInvoices?: boolean;
  showRewards?: boolean;
}

interface QueueTile {
  key: string;
  title: string;
  /** A count queue leads with the count; a money queue leads with the money. */
  value: React.ReactNode;
  summary: string;
  overdue: number;
  route: string;
  /** Router state the destination reads, e.g. which tab to open on. */
  state?: { activeTab: number };
  loading: boolean;
}

/**
 * A count read off a saturated sample is a floor, and says so. Interpolated as
 * a string, so the existing "{{count}} bills" key needs no sibling.
 */
const atLeast = (count: number, capped: boolean): number | string =>
  capped ? `${count}+` : count;

/**
 * A queue tile's caption: the overdue count in the danger tone, then what the
 * queue holds. `variant="inherit"` keeps the coloured half on the caption's own
 * type scale, which StatCard sets on the slot rather than through a variant.
 */
const QueueCaption: React.FC<{ overdue: number; summary: string }> = ({
  overdue,
  summary,
}) => {
  const { t } = useTranslation();

  if (overdue <= 0) return <>{summary}</>;

  return (
    <>
      <Typography
        component="span"
        variant="inherit"
        sx={{ color: "var(--color-text-danger)" }}
      >
        {t("{{count}} overdue", { count: overdue })}
      </Typography>
      {` · ${summary}`}
    </>
  );
};

/** The things waiting on an admin, across the top of the dashboard. */
const QueueCards: React.FC<QueueCardsProps> = ({
  cardRequests,
  payments,
  invoices,
  reimbursements,
  rewards,
  currency,
  showPayments = true,
  showInvoices = true,
  showRewards = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tiles: (QueueTile | null)[] = [
    {
      key: "cardRequests",
      title: t("Card Requests"),
      value: atLeast(cardRequests.count, cardRequests.capped),
      summary: t("to approve"),
      overdue: cardRequests.overdue,
      route: "/admin/cards",
      state: { activeTab: 2 },
      loading: cardRequests.loading,
    },
    showPayments
      ? {
          key: "payments",
          title: t("Outstanding Payments"),
          value: formatCashFull(payments.total, currency),
          summary: t("{{count}} bills", {
            count: atLeast(payments.count, payments.capped),
          }),
          overdue: payments.overdue,
          route: "/admin/payments",
          loading: payments.loading,
        }
      : null,
    showInvoices
      ? {
          key: "invoices",
          title: t("Outstanding Invoices"),
          value: formatCashFull(invoices.total, currency),
          summary: t("{{count}} invoices", {
            count: atLeast(invoices.count, invoices.capped),
          }),
          overdue: invoices.overdue,
          route: "/admin/invoices",
          loading: invoices.loading,
        }
      : null,
    {
      key: "reimbursements",
      title: t("Reimbursements Due"),
      value: formatCashFull(reimbursements.total, currency),
      summary: t("{{count}} to approve", {
        count: atLeast(reimbursements.count, reimbursements.capped),
      }),
      overdue: reimbursements.overdue,
      route: "/admin/reimbursements",
      loading: reimbursements.loading,
    },
    showRewards
      ? {
          key: "rewards",
          title: t("Rewards"),
          value: formatCashFull(rewards.total, currency),
          summary: t("Available for redemption"),
          overdue: 0,
          route: "/admin/settings/rewards",
          loading: rewards.loading,
        }
      : null,
  ];

  const shown = tiles.filter((tile): tile is QueueTile => !!tile);

  return (
    // A fixed number of tiles dividing one row, so `Columns` rather than
    // `AutoGrid`: auto-fill would lay out more tracks than there are tiles on a
    // wide viewport and leave the empty ones at the right edge.
    <Columns
      gap="var(--space-4)"
      count={{ xs: 1, sm: 2, lg: shown.length }}
      sx={{ width: "100%" }}
    >
      {shown.map((tile) => (
        <StatCard
          key={tile.key}
          title={tile.title}
          value={tile.value}
          // While loading the slot stays present but empty, so the tile does
          // not grow by a caption's height when the data lands.
          caption={
            tile.loading ? (
              ""
            ) : (
              <QueueCaption overdue={tile.overdue} summary={tile.summary} />
            )
          }
          action={{
            type: "open",
            onClick: () =>
              navigate(tile.route, tile.state && { state: tile.state }),
          }}
          loading={tile.loading}
          sx={{ height: "100%" }}
        />
      ))}
    </Columns>
  );
};

export default QueueCards;
